import React, { FC } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Cookies from 'js-cookie';

import { assetUrl, VOUCHER_COOKIE_NAME } from 'constants/global';

import { AuthenticationModal } from 'authentication/AuthenticationModal';
import { useLoadImage } from 'hooks/useLoadImage';

import { Center } from 'components/utils/Center';
import { Spinner } from 'components/Spinner';

import { VoucherInputSection } from './VoucherInputSection';
import { ConfirmSection } from './ConfirmSection';
import { LoginSection } from './LoginSection';
import { CongratulationsSection } from './CongratulationsSection';
import { Decoration } from './Decoration';

const BACKGROUND_IMAGE = assetUrl('interiors/blum-1.jpg') as string;

export const EnterCode: FC = () => {
  const [code, setCode] = React.useState(Cookies.get(VOUCHER_COOKIE_NAME));
  const [showCongratulations, setShowCongratulations] = React.useState(false);
  const isBackgroundImageLoaded = useLoadImage(BACKGROUND_IMAGE);
  const [isDecorationLoaded, setIsDecorationLoaded] = React.useState(false);

  React.useEffect(() => {
    AuthenticationModal.bindModals();
  }, []);

  React.useEffect(() => {
    if (code) {
      Cookies.set(VOUCHER_COOKIE_NAME, code);
    } else {
      Cookies.remove(VOUCHER_COOKIE_NAME);
    }
  }, [code]);

  React.useEffect(() => {
    if (showCongratulations) {
      // Clear code on congrats screen.
      setCode(undefined);
    }
  }, [showCongratulations, setCode]);

  const showEnterCode = !showCongratulations && code === undefined;
  const showLoginPrompt =
    !showCongratulations && code && !sedition.loggedInUser;
  const showConfirm = !showCongratulations && code && sedition.loggedInUser;

  return (
    <>
      {!(isBackgroundImageLoaded && isDecorationLoaded) && (
        <Center>
          <Spinner light />
        </Center>
      )}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: isBackgroundImageLoaded && isDecorationLoaded ? 1 : 0,
        }}
      >
        <div
          className='stretch'
          style={{
            backgroundImage: `url(${BACKGROUND_IMAGE})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div
          className='page-hero page-hero--full-viewport-with-navigation z-1 pos-rel'
          style={{ overflowY: 'visible', paddingBottom: '40px' }}
        >
          <div className='streth' style={{ overflowX: 'hidden' }}>
            <Decoration
              onDecorationLoaded={() => {
                setIsDecorationLoaded(true);
              }}
            />
          </div>
          <AnimatePresence exitBeforeEnter>
            {showEnterCode ? (
              <motion.div
                key='enter-voucher'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                exit={{ opacity: 0 }}
                style={{ maxWidth: 468, width: '100%' }}
              >
                <VoucherInputSection onValid={setCode} />
              </motion.div>
            ) : null}
            {showLoginPrompt ? (
              <motion.div
                key='voucher-ok'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                style={{ maxWidth: 360, width: '100%' }}
              >
                <LoginSection onBack={() => setCode(undefined)} />
              </motion.div>
            ) : null}
            {showConfirm && code ? (
              <motion.div
                key='confirm'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                style={{ maxWidth: 525, width: '100%' }}
              >
                <ConfirmSection
                  code={code ?? ''}
                  onBack={() => setCode(undefined)}
                  onClaimed={() => setShowCongratulations(true)}
                />
              </motion.div>
            ) : null}
            {showCongratulations ? (
              <motion.div
                key='confirm'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                style={{ maxWidth: 625, width: '100%' }}
              >
                <CongratulationsSection />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
};
